import { useEffect, useState } from 'react';

import { Cluster } from '@cast/types';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';

import { useDidClusterBecomeOperable } from './useDidClusterBecomeOperable';
import { useWasCredentialsAssigned } from './useWasCredentialsAssigned';
import { useWasNetworkMetricsEnabled } from './useWasNetworkMetricsEnabled';
import { useWasOmniEnabled } from './useWasOmniEnabled';
import { useWasReliabilityMetricsInstalled } from './useWasReliabilityMetricsInstalled';
import { useWasSecurityEnabled } from './useWasSecurityEnabled';
import { useWasWorkloadAutoscalerEnabled } from './useWasWorkloadAutoscalerEnabled';
import { FeatureSelection, FeatureName } from '../types';

import { isAKSCluster } from '@cast/shared-cluster-context-data-access';

type UseWereFeaturesEnabledArgs = {
  cluster: Cluster;
  featureSelection: FeatureSelection;
  enabled: boolean;
  onSuccess: () => void;
  onError: () => void;
};

type EnabledFeatures = Partial<FeatureSelection>;

export const useWerePhase2FeaturesEnabled = ({
  cluster,
  featureSelection,
  enabled,
  onSuccess,
  onError,
}: UseWereFeaturesEnabledArgs) => {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const [featuresToMonitor, setFeaturesToMonitor] = useState<EnabledFeatures>(
    {}
  );
  const hasSelectedFeatures = !!Object.keys(featuresToMonitor).length;

  const wasCredentialsAssigned = useWasCredentialsAssigned({
    clusterId: cluster.id,
    onSuccess: () => {
      if (isAKSCluster(cluster)) {
        setFeaturesToMonitor((previousFeatures) => ({
          ...previousFeatures,
          autoscaler: true,
        }));
      }
    },
    onError,
    enabled: enabled && 'autoscaler' in featuresToMonitor,
  });

  useWasSecurityEnabled({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        security: true,
      }));
    },
    onError,
    enabled: enabled && 'security' in featuresToMonitor,
  });

  useWasWorkloadAutoscalerEnabled({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        workloadAutoscaler: true,
      }));
    },
    onError,
    enabled: enabled && 'workloadAutoscaler' in featuresToMonitor,
  });

  useWasOmniEnabled({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        omni: true,
      }));
    },
    onError,
    enabled: enabled && 'omni' in featuresToMonitor,
  });

  useDidClusterBecomeOperable({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        autoscaler: true,
      }));
    },
    onError,
    enabled:
      !isAKSCluster(cluster) &&
      enabled &&
      'autoscaler' in featuresToMonitor &&
      wasCredentialsAssigned,
  });

  useWasNetworkMetricsEnabled({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        networkCostMetrics: true,
      }));
    },
    onError,
    enabled: enabled && 'networkCostMetrics' in featuresToMonitor,
  });

  useWasReliabilityMetricsInstalled({
    clusterId: cluster.id,
    onSuccess: () => {
      setFeaturesToMonitor((previousFeatures) => ({
        ...previousFeatures,
        reliabilityMetrics: true,
      }));
    },
    onError,
    enabled: enabled && 'reliabilityMetrics' in featuresToMonitor,
  });

  useEffect(() => {
    // Populate the features to monitor based on the user selection
    if (enabled && !hasSelectedFeatures) {
      const features = Object.entries(featureSelection).reduce(
        (acc, [feature, selected]) => {
          if (!selected) return acc;
          // nvidiaGpuPlugin has no enablement monitoring
          if (feature === 'nvidiaGpuPlugin') return acc;
          // TODO (Alex): Enablement detection will be implemented in UI-3586
          if (feature === 'gpuMetricsExporter') {
            acc[feature as FeatureName] = true;
          } else if (feature === 'security' && isSecuritySunsetEnabled) {
            // Auto-resolve security when sunset — skip polling
            acc[feature as FeatureName] = true;
          } else {
            // costMonitoring is always enabled
            acc[feature as FeatureName] = feature === 'costMonitoring';
          }
          return acc;
        },
        {} as EnabledFeatures
      );

      setFeaturesToMonitor(features);
    }
  }, [featureSelection, hasSelectedFeatures, enabled, isSecuritySunsetEnabled]);

  useEffect(() => {
    const allFeaturesEnabled = Object.values(featuresToMonitor).every(Boolean);

    if (enabled && hasSelectedFeatures && allFeaturesEnabled) {
      onSuccess();
    }
  }, [enabled, onSuccess, featuresToMonitor, hasSelectedFeatures]);

  return featuresToMonitor;
};
